import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { type MeetlyActionItem, type MeetlyMeeting, type MeetlyMeetingUpdate } from '../../api/meetly';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

type EditableAction = Pick<MeetlyActionItem, 'text' | 'owner' | 'task' | 'due' | 'task_created'> & {
  localKey: string;
  serverId?: string;
};

const actionFrom = (action: MeetlyActionItem): EditableAction => ({
  localKey: action.id,
  serverId: action.id,
  text: action.text || '',
  owner: action.owner || '',
  task: action.task || '',
  due: action.due || '',
  task_created: action.task_created,
});

const newAction = (): EditableAction => ({
  localKey: `new-action-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  text: '',
  owner: '',
  task: '',
  due: '',
  task_created: false,
});

const optionalValue = (value: string | null | undefined) => value?.trim() || null;

interface MeetlyMeetingEditorProps {
  meeting: MeetlyMeeting;
  onClose: () => void;
  onSave: (update: MeetlyMeetingUpdate) => Promise<void>;
}

export default function MeetlyMeetingEditor({ meeting, onClose, onSave }: MeetlyMeetingEditorProps) {
  const [title, setTitle] = useState(meeting.title || '');
  const [summary, setSummary] = useState(meeting.summary || '');
  const [actions, setActions] = useState<EditableAction[]>(() => meeting.action_items.map(actionFrom));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateAction = (index: number, patch: Partial<EditableAction>) => {
    setActions(current => current.map((action, actionIndex) => actionIndex === index ? { ...action, ...patch } : action));
  };

  const moveAction = (index: number, direction: -1 | 1) => {
    const destination = index + direction;
    if (destination < 0 || destination >= actions.length) return;
    setActions(current => {
      const next = [...current];
      [next[index], next[destination]] = [next[destination], next[index]];
      return next;
    });
  };

  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await onSave({
        title: title.trim(),
        summary,
        action_items: actions.map(({ serverId, text, owner, task, due }) => ({
          ...(serverId ? { id: serverId } : {}),
          text: text.trim(),
          owner: optionalValue(owner),
          task: optionalValue(task),
          due: optionalValue(due),
        })),
      });
      onClose();
    } catch {
      setError('Meetly could not save these changes. Your edits are still here—please try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => { if (!open && !pending) onClose(); }}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto rounded-[var(--gt-radius-card)] border-[var(--gt-border)] bg-[var(--gt-surface-0)] p-5 text-[var(--gt-text-primary)] sm:p-6">
        <DialogHeader>
          <DialogTitle className="gt-display text-xl">Edit meeting</DialogTitle>
          <DialogDescription className="text-[var(--gt-text-secondary)]">Update the meeting details and suggested actions. Tasks already created in TeamLio stay read-only.</DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-4">
          {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
          <label className="block text-sm font-semibold">
            Meeting title
            <input
              value={title}
              onChange={event => setTitle(event.target.value)}
              disabled={pending}
              className="mt-1.5 w-full rounded-[var(--gt-radius-input)] border border-[var(--gt-border)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--gt-primary)] disabled:opacity-60"
            />
          </label>
          <label className="block text-sm font-semibold">
            Summary
            <textarea
              value={summary}
              onChange={event => setSummary(event.target.value)}
              disabled={pending}
              rows={4}
              className="mt-1.5 w-full resize-y rounded-[var(--gt-radius-input)] border border-[var(--gt-border)] bg-white px-3 py-2 text-sm leading-6 outline-none focus:border-[var(--gt-primary)] disabled:opacity-60"
            />
          </label>

          <section aria-labelledby="meeting-actions-heading">
            <div className="flex items-center justify-between gap-3">
              <h3 id="meeting-actions-heading" className="text-sm font-bold">Action items</h3>
              <button type="button" onClick={() => setActions(current => [...current, newAction()])} disabled={pending} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--gt-border-strong)] bg-white px-3 py-1.5 text-xs font-semibold hover:bg-[var(--gt-surface-1)] disabled:opacity-60">
                <Plus size={14} aria-hidden="true" /> Add action
              </button>
            </div>
            <div className="mt-3 space-y-3">
              {actions.map((action, index) => {
                const readOnly = action.task_created;
                return (
                  <div key={action.localKey} className="rounded-xl border border-[var(--gt-border)] bg-[var(--gt-surface-1)] p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-[var(--gt-text-secondary)]">Action {index + 1}{readOnly ? ' · Task created' : ''}</span>
                      {!readOnly && (
                        <span className="flex items-center gap-1">
                          <button type="button" aria-label={`Move action ${index + 1} up`} onClick={() => moveAction(index, -1)} disabled={pending || index === 0} className="rounded p-1 hover:bg-white disabled:opacity-35"><ChevronUp size={15} aria-hidden="true" /></button>
                          <button type="button" aria-label={`Move action ${index + 1} down`} onClick={() => moveAction(index, 1)} disabled={pending || index === actions.length - 1} className="rounded p-1 hover:bg-white disabled:opacity-35"><ChevronDown size={15} aria-hidden="true" /></button>
                          <button type="button" aria-label={`Remove action ${index + 1}`} onClick={() => setActions(current => current.filter((_, actionIndex) => actionIndex !== index))} disabled={pending} className="rounded p-1 text-red-700 hover:bg-red-50 disabled:opacity-35"><Trash2 size={15} aria-hidden="true" /></button>
                        </span>
                      )}
                    </div>
                    <label className="sr-only" htmlFor={`action-${action.localKey}`}>Action {index + 1}</label>
                    <input id={`action-${action.localKey}`} value={action.text} onChange={event => updateAction(index, { text: event.target.value })} disabled={pending || readOnly} className="w-full rounded-lg border border-[var(--gt-border)] bg-white px-2.5 py-2 text-sm disabled:cursor-not-allowed disabled:bg-[var(--gt-surface-2)]" placeholder="What needs to happen?" />
                    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                      <input aria-label={`Owner for action ${index + 1}`} value={action.owner || ''} onChange={event => updateAction(index, { owner: event.target.value })} disabled={pending || readOnly} className="rounded-lg border border-[var(--gt-border)] bg-white px-2.5 py-2 text-xs disabled:cursor-not-allowed disabled:bg-[var(--gt-surface-2)]" placeholder="Owner" />
                      <input aria-label={`Task for action ${index + 1}`} value={action.task || ''} onChange={event => updateAction(index, { task: event.target.value })} disabled={pending || readOnly} className="rounded-lg border border-[var(--gt-border)] bg-white px-2.5 py-2 text-xs disabled:cursor-not-allowed disabled:bg-[var(--gt-surface-2)]" placeholder="Task" />
                      <input aria-label={`Due date for action ${index + 1}`} value={action.due || ''} onChange={event => updateAction(index, { due: event.target.value })} disabled={pending || readOnly} className="rounded-lg border border-[var(--gt-border)] bg-white px-2.5 py-2 text-xs disabled:cursor-not-allowed disabled:bg-[var(--gt-surface-2)]" placeholder="Due date" />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <DialogFooter className="gap-2 sm:gap-2">
            <button type="button" onClick={onClose} disabled={pending} className="min-h-10 rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-sm font-semibold hover:bg-[var(--gt-surface-1)] disabled:opacity-60">Cancel</button>
            <button type="submit" disabled={pending} className="min-h-10 rounded-full bg-[var(--gt-primary)] px-4 py-2 text-sm font-bold text-white disabled:cursor-wait disabled:opacity-60">{pending ? 'Saving…' : 'Save changes'}</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
